import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import SmartImage from "@/components/base/SmartImage";

// Replaceable media slots — swap with dedicated Our Story assets when available.
const STORY_HERO = "https://public.readdy.ai/ai/img_res/sandbox/9ba4bf4afd2936b8151e5d4e41d6be7f/83987786-4265-4e67-a199-9d6d22191d2a_our-story-hero.webp";
const WHY_IMAGE = "https://readdy.ai/api/search-image?query=Quiet%20still%20life%20of%20a%20folded%20technical%20outdoor%20jacket%20on%20a%20raw%20wooden%20shelf%20next%20to%20a%20small%20notebook%20and%20a%20worn%20pencil%2C%20soft%20north-facing%20window%20light%2C%20muted%20neutral%20palette%20of%20stone%20grey%20and%20warm%20beige%2C%20restrained%20editorial%20studio%20photography%2C%20calm%20atmosphere%2C%20fine%20textile%20detail%2C%20matte%20high%20detail%20image&width=1400&height=1200&seq=our-story-why-we-exist-placeholder-01&orientation=portrait";
const MATERIAL_MAKING = "https://public.readdy.ai/ai/img_res/sandbox/9ba4bf4afd2936b8151e5d4e41d6be7f/83987786-4265-4e67-a199-9d6d22191d2a_our-story-material.webp";
const FIELD_TESTED = "https://public.readdy.ai/ai/img_res/sandbox/9ba4bf4afd2936b8151e5d4e41d6be7f/83987786-4265-4e67-a199-9d6d22191d2a_our-story-field.webp";

// One shared height token for every editorial split section — guarantees identical proportions.
const SPLIT_H = "h-auto min-h-[380px] md:h-[540px]";
const SPLIT_IMG_H = "h-[300px] md:h-[540px]";
const SPLIT_PAD = "px-5 md:px-10 lg:px-14 py-10 md:py-0";

export default function OurStoryPage() {
  return (
    <div className="min-h-screen bg-background-50 text-foreground-100 flex flex-col">
      <SiteHeader variant="overlay" />
      <main className="flex-1">
        {/* Hero — only large campaign image on the page */}
        <section className="relative w-full h-[480px] md:h-[720px] overflow-hidden bg-background-100">
          <SmartImage src={STORY_HERO} alt="Northvale — outdoor essentials" tone="dark" eager imgClassName="absolute inset-0 w-full h-full object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/20 to-black/55"></div>
          <div className="relative z-10 h-full w-full px-5 md:px-10 pb-12 md:pb-20 flex flex-col justify-end">
            <p className="text-[10px] tracking-[0.24em] uppercase text-foreground-100/85 mb-4">Our Story</p>
            <h1 className="text-[32px] md:text-[54px] leading-[1.08] md:leading-[1.02] tracking-[-0.02em] text-foreground-50 font-light max-w-4xl">
              Fewer things. Made carefully. Tested outside.
            </h1>
            <p className="text-[16px] md:text-[15px] text-foreground-100/85 leading-relaxed max-w-lg mt-5 font-light">
              Northvale is a small outdoor studio building the essentials we want to live in — for the trail, the shoulder season, and everything in between.
            </p>
          </div>
        </section>

        {/* 01 — Why we exist: text left / image right */}
        <section className="bg-background-50 border-t border-white/5">
          <div className="grid grid-cols-1 lg:grid-cols-2 items-stretch">
            <div className={`${SPLIT_PAD} flex flex-col justify-center ${SPLIT_H} order-2 lg:order-1`}>
              <p className="text-[10px] tracking-[0.24em] uppercase text-foreground-400 mb-4">01 — Why we exist</p>
              <h2 className="text-[28px] md:text-[30px] leading-[1.15] tracking-[-0.015em] text-foreground-50 font-light mb-5 max-w-md">
                A quieter kind of outdoor brand.
              </h2>
              <div className="space-y-4 text-[16px] md:text-[15px] text-foreground-300 leading-[1.7] font-light max-w-md">
                <p>Most outdoor closets are full of things worn twice. We started Northvale to make the opposite — a short, honest range of pieces you actually reach for, season after season.</p>
                <p>Every piece works in changing weather, earns its place in a pack, and completes a full field cycle before it earns a name.</p>
              </div>
            </div>
            <div className={`relative w-full ${SPLIT_IMG_H} overflow-hidden bg-background-200 order-1 lg:order-2`}>
              <SmartImage src={WHY_IMAGE} alt="Why we exist" tone="dark" imgClassName="absolute inset-0 w-full h-full object-cover object-center" />
            </div>
          </div>
        </section>

        {/* 02 — Material philosophy: image left / text right */}
        <section className="bg-background-100 border-t border-white/5">
          <div className="grid grid-cols-1 lg:grid-cols-2 items-stretch">
            <div className={`relative w-full ${SPLIT_IMG_H} overflow-hidden bg-background-200`}>
              <SmartImage src={MATERIAL_MAKING} alt="Material philosophy" tone="dark" imgClassName="absolute inset-0 w-full h-full object-cover object-center" />
            </div>
            <div className={`${SPLIT_PAD} flex flex-col justify-center ${SPLIT_H}`}>
              <p className="text-[10px] tracking-[0.24em] uppercase text-foreground-400 mb-4">02 — Material philosophy</p>
              <h2 className="text-[28px] md:text-[30px] leading-[1.12] md:leading-[1.1] tracking-[-0.015em] text-foreground-50 font-light mb-6 max-w-md">
                Three beliefs we won't compromise.
              </h2>
              <ul className="divide-y divide-white/10 mb-8 max-w-md">
                {[
                  ["Weather Protection", "Shells that shed rain quietly, year after year."],
                  ["Breathability", "Fabrics that move air, not just water."],
                  ["Construction", "Seams and hardware built to be repaired, not replaced."],
                ].map(([t, d]) => (
                  <li key={t} className="py-3.5 flex items-baseline justify-between gap-6">
                    <span className="text-[14.5px] text-foreground-50 font-light">{t}</span>
                    <span className="text-[12px] text-foreground-300 text-right max-w-[240px]">{d}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/technology"
                className="text-[12px] tracking-[0.15em] uppercase text-foreground-50 hover:text-primary-400 transition cursor-pointer w-fit inline-flex items-center gap-2 border-b border-foreground-300/50 hover:border-primary-400 pb-1"
              >
                Explore Technology <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* 03 — Field tested: text left / image right */}
        <section className="bg-background-50 border-t border-white/5">
          <div className="grid grid-cols-1 lg:grid-cols-2 items-stretch">
            <div className={`${SPLIT_PAD} flex flex-col justify-center ${SPLIT_H} order-2 lg:order-1`}>
              <p className="text-[10px] tracking-[0.24em] uppercase text-foreground-400 mb-4">03 — Field tested</p>
              <h2 className="text-[28px] md:text-[30px] leading-[1.12] md:leading-[1.1] tracking-[-0.015em] text-foreground-50 font-light mb-5 max-w-md">
                Outside longer than most catalogs.
              </h2>
              <div className="space-y-4 text-[16px] md:text-[15px] text-foreground-300 leading-[1.7] font-light max-w-md">
                <p>Nine days on the ridge. Five on the coast. A shoulder season of quiet rain. A piece only earns its name after the notes come back.</p>
                <p>Field feedback shapes the final pattern, the hardware, the hood brim — the details you never see, but always feel.</p>
              </div>
            </div>
            <div className={`relative w-full ${SPLIT_IMG_H} overflow-hidden bg-background-200 order-1 lg:order-2`}>
              <SmartImage src={FIELD_TESTED} alt="Field tested" tone="dark" imgClassName="absolute inset-0 w-full h-full object-cover object-center" />
            </div>
          </div>
        </section>

        {/* 04 — Closing CTA */}
        <section className="bg-background-100 py-12 md:py-20 border-t border-white/5">
          <div className="w-full px-5 md:px-10 flex flex-col md:flex-row md:items-end md:justify-between gap-6 md:gap-10 max-w-[1400px] mx-auto">
            <h2 className="text-[28px] md:text-[36px] leading-[1.12] md:leading-[1.1] tracking-[-0.015em] text-foreground-50 font-light max-w-2xl">
              Built once. Used for years.
            </h2>
            <Link
              to="/shop"
              className="text-[12px] tracking-[0.18em] uppercase text-foreground-50 hover:text-primary-400 border-b border-foreground-300/50 hover:border-primary-400 pb-1 inline-flex items-center gap-2 cursor-pointer w-fit whitespace-nowrap"
            >
              Shop the collection <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}